import { clsx } from "clsx";
import type { HTMLAttributes } from "react";
import { Button } from "../Button/Button";
import { Icon } from "../Icons/Icons";
import { Body } from "../Typography/Body";
import "./PDFDownloadBar.css";

export interface PDFDownloadBarProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  /**
   * File name shown next to the document icon (e.g. "Navigating Indigo portal.pdf").
   */
  fileName: string;
  /**
   * Invoked when the user clicks the download action.
   */
  onDownload: () => void;
  /**
   * Label for the download action.
   * @default "Download"
   */
  downloadLabel?: string;
  /**
   * Disables the download action (e.g. while the file is still loading).
   */
  disabled?: boolean;
}

/**
 * PDFDownloadBar
 *
 * A slim bar shown above an embedded PDF viewer: a document icon + file name on
 * the left, and a naked download action on the right. Mirrors the Figma
 * "download row" used on Library slide-deck / guide pages.
 */
export const PDFDownloadBar = ({
  fileName,
  onDownload,
  downloadLabel = "Download",
  disabled = false,
  className,
  ...rest
}: PDFDownloadBarProps) => (
  <div className={clsx("indigo-pdf-download-bar", className)} {...rest}>
    <div className="indigo-pdf-download-bar__file">
      <span className="indigo-pdf-download-bar__file-icon" aria-hidden="true">
        <Icon name="file" size="s" />
      </span>
      <Body size="md" className="indigo-pdf-download-bar__file-name">
        {fileName}
      </Body>
    </div>
    <Button
      variant="ghost"
      size="sm"
      icon={<Icon name="downloads" size="s" />}
      onClick={onDownload}
      disabled={disabled}
      className="indigo-pdf-download-bar__action"
    >
      {downloadLabel}
    </Button>
  </div>
);

PDFDownloadBar.displayName = "PDFDownloadBar";

export default PDFDownloadBar;
